<script setup lang="ts">
import { computed } from "vue";
import { z } from "zod";

const props = defineProps<{
  email: string;
  confirmEmail: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:email", value: string): void;
  (e: "update:confirmEmail", value: string): void;
}>();

const emailSchema = z.string().email();

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

const normalizedEmail = computed(() => normalize(props.email));
const normalizedConfirm = computed(() => normalize(props.confirmEmail));

const emailValid = computed(() => emailSchema.safeParse(normalizedEmail.value).success);
const confirmValid = computed(
  () => emailSchema.safeParse(normalizedConfirm.value).success,
);
const matches = computed(() => normalizedEmail.value === normalizedConfirm.value);

const showEmailError = computed(
  () => !emailValid.value && props.email.length > 0,
);
const showMismatchError = computed(
  () => confirmValid.value && emailValid.value && !matches.value,
);

const isReady = computed(
  () => emailValid.value && confirmValid.value && matches.value,
);

defineExpose({
  isReady,
  normalizedEmail,
});

function onEmailInput(e: Event) {
  emit("update:email", (e.target as HTMLInputElement).value);
}
function onConfirmInput(e: Event) {
  emit("update:confirmEmail", (e.target as HTMLInputElement).value);
}
</script>

<template>
  <fieldset class="flex flex-col gap-3" :disabled="disabled">
    <div class="flex flex-col gap-1">
      <label for="buy-email" class="text-sm font-medium text-slate-800">
        {{ $t("buy.email.label") }}
      </label>
      <input
        id="buy-email"
        type="email"
        autocomplete="email"
        inputmode="email"
        spellcheck="false"
        required
        class="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-base focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        :value="email"
        @input="onEmailInput"
      />
      <p v-if="showEmailError" class="text-sm text-red-700" data-testid="email-error">
        {{ $t("buy.email.invalid") }}
      </p>
      <p v-else class="text-xs text-slate-500">{{ $t("buy.email.hint") }}</p>
    </div>

    <div class="flex flex-col gap-1">
      <label for="buy-email-confirm" class="text-sm font-medium text-slate-800">
        {{ $t("buy.email.confirmLabel") }}
      </label>
      <input
        id="buy-email-confirm"
        type="email"
        autocomplete="email"
        inputmode="email"
        spellcheck="false"
        required
        class="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-base focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        :value="confirmEmail"
        @input="onConfirmInput"
      />
      <p
        v-if="showMismatchError"
        class="text-sm text-red-700"
        data-testid="email-mismatch-error"
      >
        {{ $t("buy.email.mismatch") }}
      </p>
    </div>
  </fieldset>
</template>
